import { IdleQueue } from 'idlize/IdleQueue.mjs';
import gsap from 'gsap';
import type { IView } from '@barba/core';
import { createInitialMainPageTimeline } from '@/animations/pages/main/initial-main';
import scrollFullscreen from '@/inits/scroll-fullscreen';
import heroMainAnimation from '../animations/pages/main/hero-main';
import articleVideos from '@/inits/article-videos';
import headerCartHandlers from '@/inits/components/header/header-cart-handlers';
import reviewsPopupsModule, { homePagePlayingPlayer } from '@/inits/pages/index/reviews-popups';
import contactPopupModule from '@/inits/pages/index/contact-popup';
import saleCounter from '@/inits/sale-counter';
import successRegistrationPopup from '@/inits/pages/index/success-registration-popup';
import salePopup from '@/inits/sale-popup';

const beforeEnterQueue = new IdleQueue();
let enterTl: gsap.core.Timeline | null;
let marqueeTween: gsap.core.Tween | null;
let spacerTween: gsap.core.Tween | null;
const timerSlides = document.querySelectorAll<HTMLElement>('.js-timer-slide');
export default {
    namespace: 'index-page',

    beforeEnter({ next }) {
        beforeEnterQueue.pushTask(() => {
            heroMainAnimation.init();
        });

        beforeEnterQueue.pushTask(() => {
            timerSlides.forEach((slide) => {
                saleCounter.init(slide);
                if (timerSlides.length === 1) {
                    slide.classList.add('swiper-slide-active');
                }
            });
        });

        beforeEnterQueue.pushTask(() => {
            scrollFullscreen.init(next.container);
        });

        beforeEnterQueue.pushTask(() => {
            const testimonialsImagesSpacer = next.container.querySelector('.js-main-testimonials__images-spacer');

            enterTl = createInitialMainPageTimeline(next.container);

            gsap.from('.js-main-story-marquee', {
                ease: 'power1.easeOut',
                duration: 1.4,
                x: '100vw',
                onComplete: () => {
                    gsap.set('.main-story__marquee', {
                        animation: 'marquee-text-animation 30s linear infinite',
                    });
                },
                scrollTrigger: {
                    trigger: '.js-main-story-marquee',
                    start: 'top bottom-=30%',
                },
            });

            if (testimonialsImagesSpacer) {
                const images = Array.from(document.querySelectorAll('.js-main-testimonials__image'));

                spacerTween = gsap.fromTo(
                    images,
                    { y: '-100vh' },
                    {
                        duration: 1.5,
                        ease: 'bounce.out',
                        y: 0,
                        delay: (i) => i * 0.06,
                        scrollTrigger: {
                            trigger: testimonialsImagesSpacer,
                            start: 'center bottom',
                        },
                        onStart: () => {
                            images.forEach((img, i) => {
                                setTimeout(() => {
                                    img.classList.add('main-testimonials__image-wrapper--visible');
                                }, i * 6);
                            });
                        },
                    },
                );
            }
        });

        beforeEnterQueue.pushTask(() => {
            articleVideos.init();
        });

        beforeEnterQueue.pushTask(() => {
            headerCartHandlers.initForPage();
        });

        beforeEnterQueue.pushTask(() => {
            reviewsPopupsModule.init();
        });

        beforeEnterQueue.pushTask(() => {
            contactPopupModule.init();
        });

        beforeEnterQueue.pushTask(() => {
            successRegistrationPopup.init();
        });

        beforeEnterQueue.pushTask(() => {
            salePopup.init();
        });

        beforeEnterQueue.pushTask(() => {
            document.documentElement.classList.add('index-page');
        });
    },

    afterEnter() {
        beforeEnterQueue.pushTask(() => {
            enterTl?.play();
        });
    },

    beforeLeave() {
        beforeEnterQueue.clearPendingTasks();

        document.documentElement.classList.remove('index-page');

        heroMainAnimation.destroy();

        if (marqueeTween) {
            marqueeTween.scrollTrigger?.kill();
            marqueeTween.kill();
            marqueeTween = null;
        }

        if (spacerTween) {
            spacerTween.scrollTrigger?.kill();
            spacerTween.kill();
            spacerTween = null;
        }

        articleVideos.destroy();
        headerCartHandlers.destroyForPage();
        reviewsPopupsModule.destroy();
        contactPopupModule.destroy();
        saleCounter.destroy();
        successRegistrationPopup.destroy();

        if (homePagePlayingPlayer && homePagePlayingPlayer.isPlaying?.()) {
            homePagePlayingPlayer.stop();
        }
    },

    afterLeave({ current }) {
        scrollFullscreen.destroy(current.container);
    },
} as IView;
